import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ContactModal = ({ isOpen, onClose }: ContactModalProps) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    message: ''
  });
  const { toast } = useToast();

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.email || !formData.message) {
      toast({
        title: t('contact.validationError'),
        description: t('contact.fillRequiredFields'),
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Server builds and escapes the email; recipient is fixed server-side.
      const { error } = await supabase.functions.invoke('send-email-unified', {
        body: {
          type: 'contact',
          name: formData.fullName,
          email: formData.email,
          phone: formData.phoneNumber,
          message: formData.message.slice(0, 2000),
          pageUrl: window.location.origin + window.location.pathname,
        }
      });

      if (error) {
        throw error;
      }

      toast({
        title: t('contact.messageSent'),
        description: t('contact.messageSentDescription'),
      });

      // Reset form and close modal
      setFormData({
        fullName: '',
        email: '',
        phoneNumber: '',
        message: ''
      });
      onClose();

    } catch (error) {
      console.error('Contact form submission error:', error);
      toast({
        title: t('contact.error'),
        description: t('contact.sendFailed'),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('contact.title')}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="fullName">{t('contact.fullName')} *</Label>
            <Input
              id="fullName"
              value={formData.fullName}
              onChange={(e) => handleInputChange('fullName', e.target.value)}
              placeholder={t('contact.fullNamePlaceholder')}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="email">{t('contact.email')} *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder={t('contact.emailPlaceholder')}
              required
            />
          </div>
          
          <div>
            <Label htmlFor="phoneNumber">{t('contact.phoneNumber')}</Label>
            <Input
              id="phoneNumber"
              type="tel"
              value={formData.phoneNumber}
              onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
              placeholder={t('contact.phoneNumberPlaceholder')}
            />
          </div>
          
          <div>
            <Label htmlFor="message">{t('contact.message')} *</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              placeholder={t('contact.messagePlaceholder')}
              className="min-h-24"
              required
            />
          </div>
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('contact.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('contact.sending')}
                </>
              ) : (
                t('contact.sendMessage')
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};